import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _MetadataClient;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import NetworkStatus from "../resilienceQueue/NetworkStatus";
import { DEFAULT_POLLING_OPTIONS } from "../resilienceQueue/PullBatchableQueue";
import Scheduler, { DoneState } from "../resilienceQueue/scheduler";
import { NetworkStatusEnum } from "../resilienceQueue/types";
import { isStargateProxyPath } from "../selectHost";
import { TestSupport } from "../util/testSupport";
import { DEFAULT_METADATA_PROPERTY_CHANGE_REFRESH_INTERVAL, DEFAULT_METADATA_REFRESH_INTERVAL, DEFAULT_METADATA_REQUEST_TIMEOUT } from "./defaults";
import { fetchMetadata } from "./fetchMetadata";
import { ALLOW_LISTED_FETCH_ERROR_NAMES, FetchError, MetadataResponse as MetadataInfo } from "./types";
export class MetadataClient {
  constructor(apiHostProtocol, apiHost, product) {
    this._userInfo = {
      anonymousId: ''
    };
    this._tenantInfo = {};
    this._url = `${apiHostProtocol}://${apiHost}/metadata`;
    this._product = product;
    this._metadataInfo = null;
    this._requestStatus = 'PENDING';
    this._lastFetchRequestCount = 0;
    this._lastErrorReason = null;
    this._fetchAbortController = null;
    this._hasBeenStopped = false;
    this._isEnabled = false;
    this._propertyChangeRefreshTimeout = null;
    const isStargateProxy = isStargateProxyPath({
      apiHost: apiHost
    });
    this._metadataHost = isStargateProxy ? 'PRODUCT_HOST' : apiHost;
    this._options = this.buildOptions({
      backoffFactor: 2,
      backoffJitterPercentage: 0.2,
      batchFlushSize: 7,
      flushBeforeUnload: false,
      flushWaitMs: 500,
      maxAttempts: 10,
      maxItems: 1000,
      maxRetryDelay: 30000,
      minRetryDelay: 1000
    });
    this._scheduler = new Scheduler(_objectSpread(_objectSpread({}, this._options), {}, {
      waitInterval: DEFAULT_METADATA_REFRESH_INTERVAL
    }), this.scheduleCallback.bind(this));
    this._networkStatus = null;
  }

  /**
   * Starts the metadata client. This will start the scheduler and begin
   * fetching metadata.
   *
   * @param allowRestart - If false, the client will not be restarted if it has already been stopped
   *						 by a call to stop().
   */
  start(allowRestart) {
    if (this._isEnabled || this._hasBeenStopped && !allowRestart) {
      return;
    }
    this._isEnabled = true;
    this.startNetworkStatusMonitor();
    setTimeout(() => {
      this._scheduler.schedule({
        immediate: true
      });
    }, MetadataClient.INITIAL_REFRESH_DELAY);
  }
  stop() {
    var _this$_fetchAbortCont;
    if (!this._isEnabled) {
      return;
    }
    this._hasBeenStopped = true;
    this._isEnabled = false;
    this.stopNetworkStatusMonitor();
    this._scheduler.stop();
    (_this$_fetchAbortCont = this._fetchAbortController) === null || _this$_fetchAbortCont === void 0 || _this$_fetchAbortCont.abort();
    this.resetPropertyChangeRefresh();
  }
  startNetworkStatusMonitor() {
    if (this._networkStatus) {
      return;
    }
    this._networkStatus = new NetworkStatus(status => {
      if (status === NetworkStatusEnum.OFFLINE) {
        this._scheduler.stop();
      } else if (this._isEnabled) {
        this._scheduler.schedule({
          immediate: this._metadataInfo === null
        });
      }
    });
  }
  stopNetworkStatusMonitor() {
    if (this._networkStatus) {
      this._networkStatus.removeListeners();
      this._networkStatus = null;
    }
  }
  async refreshMetadataAsync() {
    var _this$_fetchAbortCont2;
    if (TestSupport.areFetchCallsDisabled() || !this._isEnabled) {
      return Promise.resolve();
    }
    (_this$_fetchAbortCont2 = this._fetchAbortController) === null || _this$_fetchAbortCont2 === void 0 || _this$_fetchAbortCont2.abort();
    this._fetchAbortController = new AbortController();
    const fetchAbortSignal = this._fetchAbortController.signal;
    this._lastFetchRequestCount = this._scheduler.getFailureCount();
    await fetchMetadata(this._url, {
      product: this._product,
      userInfo: this._userInfo,
      tenantInfo: this._tenantInfo
    }, DEFAULT_METADATA_REQUEST_TIMEOUT, fetchAbortSignal).then(async response => {
      const metadata = await response.json();
      this._metadataInfo = MetadataInfo.fromJson(metadata);
      this._lastFetchRequestCount++;
      this._requestStatus = 'FETCHED';
      this._lastErrorReason = null;
    }).catch(error => {
      if (fetchAbortSignal.aborted) {
        // This request was aborted, so we don't need to handle it.
        return;
      }
      this._requestStatus = 'FAILED';
      if (error instanceof FetchError && [429, 503].includes(error.statusCode)) {
        this._lastErrorReason = 'ServerBusyError';
      } else {
        this._lastErrorReason = error instanceof Error && ALLOW_LISTED_FETCH_ERROR_NAMES.includes(error.name) ? error.name : 'Unknown';
      }
      throw error;
    });
  }
  getMetadataAsync() {
    return Promise.resolve(this._metadataInfo);
  }
  get status() {
    return this._requestStatus;
  }
  get lastErrorReason() {
    return this._lastErrorReason;
  }
  get lastFetchRequestCount() {
    return this._lastFetchRequestCount;
  }
  get userInfo() {
    return this._userInfo;
  }
  set userInfo(userInfo) {
    var _this$_userInfo, _this$_userInfo2;
    if (((_this$_userInfo = this._userInfo) === null || _this$_userInfo === void 0 ? void 0 : _this$_userInfo.userId) === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.userId) && ((_this$_userInfo2 = this._userInfo) === null || _this$_userInfo2 === void 0 ? void 0 : _this$_userInfo2.userIdType) === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.userIdType)) {
      return;
    }
    this._userInfo = userInfo;
    this.schedulePropertyChangeRefresh();
  }
  get tenantInfo() {
    return this._tenantInfo;
  }
  set tenantInfo(tenantInfo) {
    var _this$_tenantInfo, _this$_tenantInfo2;
    if (((_this$_tenantInfo = this._tenantInfo) === null || _this$_tenantInfo === void 0 ? void 0 : _this$_tenantInfo.tenantId) === (tenantInfo === null || tenantInfo === void 0 ? void 0 : tenantInfo.tenantId) && ((_this$_tenantInfo2 = this._tenantInfo) === null || _this$_tenantInfo2 === void 0 ? void 0 : _this$_tenantInfo2.tenantIdType) === (tenantInfo === null || tenantInfo === void 0 ? void 0 : tenantInfo.tenantIdType)) {
      return;
    }
    this._tenantInfo = tenantInfo;
    this.schedulePropertyChangeRefresh();
  }
  get metrics() {
    return {
      host: this._metadataHost,
      status: this.status,
      lastErrorReason: this.lastErrorReason,
      lastRefreshRequestCount: this.lastFetchRequestCount
    };
  }
  async scheduleCallback(done) {
    var _this$_networkStatus;
    if (((_this$_networkStatus = this._networkStatus) === null || _this$_networkStatus === void 0 ? void 0 : _this$_networkStatus.getNetworkStatus()) === NetworkStatusEnum.OFFLINE) {
      done(DoneState.NOOP);
      return;
    }
    try {
      await this.refreshMetadataAsync();
      done(DoneState.SUCCESS);
      this._scheduler.schedule();
    } catch (error) {
      done(DoneState.ERROR);
    }
  }

  /**
   * Schedules a refresh of the metadata when the user or tenant info changes. There is
   * a short delay to allow for multiple changes to be batched together, such as when the
   * callers sets the user info and tenant info in quick succession.
   */
  schedulePropertyChangeRefresh() {
    if (!this._isEnabled) {
      return;
    }
    this.resetPropertyChangeRefresh();
    this._propertyChangeRefreshTimeout = setTimeout(() => {
      this.resetPropertyChangeRefresh();
      this._scheduler.schedule({
        immediate: true
      });
    }, DEFAULT_METADATA_PROPERTY_CHANGE_REFRESH_INTERVAL);
  }
  resetPropertyChangeRefresh() {
    if (this._propertyChangeRefreshTimeout) {
      clearTimeout(this._propertyChangeRefreshTimeout);
      this._propertyChangeRefreshTimeout = null;
    }
  }
  buildOptions(options) {
    return {
      backoffFactor: (options === null || options === void 0 ? void 0 : options.backoffFactor) || DEFAULT_POLLING_OPTIONS.backoffFactor,
      backoffJitterPercentage: (options === null || options === void 0 ? void 0 : options.backoffJitterPercentage) !== undefined ? options.backoffJitterPercentage : DEFAULT_POLLING_OPTIONS.backoffJitterPercentage,
      batchFlushSize: (options === null || options === void 0 ? void 0 : options.batchFlushSize) || DEFAULT_POLLING_OPTIONS.batchFlushSize,
      flushBeforeUnload: (options === null || options === void 0 ? void 0 : options.flushBeforeUnload) || DEFAULT_POLLING_OPTIONS.flushBeforeUnload,
      flushWaitMs: (options === null || options === void 0 ? void 0 : options.flushWaitMs) || DEFAULT_POLLING_OPTIONS.flushWaitMs,
      maxItems: (options === null || options === void 0 ? void 0 : options.maxItems) || DEFAULT_POLLING_OPTIONS.maxItems,
      maxAttempts: (options === null || options === void 0 ? void 0 : options.maxAttempts) || DEFAULT_POLLING_OPTIONS.maxAttempts,
      maxRetryDelay: (options === null || options === void 0 ? void 0 : options.maxRetryDelay) || DEFAULT_POLLING_OPTIONS.maxRetryDelay,
      minRetryDelay: (options === null || options === void 0 ? void 0 : options.minRetryDelay) || DEFAULT_POLLING_OPTIONS.minRetryDelay
    };
  }
}
_MetadataClient = MetadataClient;
_defineProperty(MetadataClient, "INITIAL_REFRESH_DELAY", 1);
_defineProperty(MetadataClient, "Factory", (apiHostProtocol, apiHost, product) => {
  return new _MetadataClient(apiHostProtocol, apiHost, product);
});